import { useEffect } from 'react';
import Box from '@mui/material/Box';
import Chip, { ChipProps } from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import LocalHospitalOutlined from '@mui/icons-material/LocalHospitalOutlined';
import BadgeOutlined from '@mui/icons-material/BadgeOutlined';
import BusinessOutlined from '@mui/icons-material/BusinessOutlined';
import CheckCircleOutlined from '@mui/icons-material/CheckCircleOutlined';
import HighlightOffOutlined from '@mui/icons-material/HighlightOffOutlined';
import PinDropOutlined from '@mui/icons-material/PinDropOutlined';
import SourceOutlined from '@mui/icons-material/SourceOutlined';
import { useParams } from 'react-router-dom';
import { Loader } from '@omnigenbiodata/ui';

import PortalLayout from '../../../../../../components/layouts/PortalLayout';
import { useAppDispatch, useAppSelector } from '../../../../../../store';
import { getSiteThunk, isBusySelector, responseSelector } from '../../../../../../store/site';
import DetailRow from './components/DetailRow';
import PageBanner from '../../../../../../components/layouts/PageBanner';

interface SiteFeatureCardProps {
  label: string;
  value: string;
  enabled?: boolean;
}

function getSiteStatusColour(initiated: boolean | null | undefined): ChipProps['color'] {
  if (initiated === true) {
    return 'success';
  }

  if (initiated === false) {
    return 'warning';
  }

  return 'default';
}

function renderSiteStatus(initiated: boolean | null | undefined) {
  if (initiated === null || initiated === undefined) {
    return <Chip label="UNKNOWN" color="default" size="small" sx={{ fontWeight: 700 }} />;
  }

  return (
    <Chip
      label={initiated ? 'INITIATED' : 'UNINITIATED'}
      color={getSiteStatusColour(initiated)}
      size="small"
      sx={{ fontWeight: 700 }}
    />
  );
}

function SiteFeatureCard({ label, value, enabled = true }: SiteFeatureCardProps) {
  const Icon = enabled ? CheckCircleOutlined : HighlightOffOutlined;

  return (
    <Box
      sx={{
        textAlign: 'center',
        p: 1.5,
        borderRadius: 2,
        bgcolor: enabled ? 'success.lighter' : 'warning.lighter',
        border: 1,
        borderColor: enabled ? 'success.light' : 'warning.light',
        minWidth: 90,
        height: '100%',
      }}
    >
      <Box
        sx={{
          width: 44,
          height: 44,
          mx: 'auto',
          mb: 1,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.paper',
        }}
      >
        <Icon
          color={enabled ? 'success' : 'warning'}
          sx={{
            fontSize: 30,
            display: 'block',
          }}
        />
      </Box>

      <Typography
        variant="subtitle2"
        component="p"
        sx={{
          fontWeight: 700,
          lineHeight: 1.2,
        }}
      >
        {label}
      </Typography>

      <Typography variant="caption" component="p" color="text.secondary" sx={{ mt: 0.25 }}>
        {value}
      </Typography>
    </Box>
  );
}

function SiteScene() {
  const dispatch = useAppDispatch();

  const { code } = useParams<{
    code: string;
  }>();

  const isBusy = useAppSelector(isBusySelector);
  const response = useAppSelector(responseSelector);

  useEffect(() => {
    if (code) {
      dispatch(getSiteThunk(code));
    }
  }, [dispatch, code]);

  const addressLines = response
    ? [response.address1, response.address2, response.postcode].filter((line): line is string => Boolean(line))
    : [];

  const isInitiated = response?.initiated === true;
  const toneColor = isInitiated ? 'success' : 'warning';

  return (
    <PortalLayout>
      <Loader isVisible={isBusy} label="Loading Site" />
      {response && !isBusy && (
        <Box pb={2}>
          <PageBanner title="Enrolment Site" description="Check the details of the current site" />

          <Paper
            elevation={4}
            sx={{
              border: 1,
              borderColor: 'rgba(16, 53, 68, 0.18)',
              bgcolor: 'background.paper',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                height: 6,
                bgcolor: 'primary.main',
              }}
            />

            <Box p={{ xs: 2, md: 4 }}>
              <Grid container spacing={3} alignItems="center">
                <Grid item xs={12} md>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      minWidth: 0,
                    }}
                  >
                    <Box
                      sx={{
                        width: 86,
                        height: 86,
                        borderRadius: '50%',
                        bgcolor: isInitiated ? 'success.lighter' : 'warning.lighter',
                        border: 1,
                        borderColor: isInitiated ? 'success.light' : 'warning.light',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <LocalHospitalOutlined color={toneColor} sx={{ fontSize: 46 }} />
                    </Box>

                    <Box sx={{ minWidth: 0 }}>
                      <Box sx={{ mb: 0.75 }}>{renderSiteStatus(response.initiated)}</Box>

                      <Typography
                        variant="h4"
                        component="h2"
                        sx={{
                          lineHeight: 1,
                          fontWeight: 700,
                          overflowWrap: 'anywhere',
                        }}
                      >
                        {response.name || 'Unknown site'}
                      </Typography>

                      <Typography
                        variant="h6"
                        component="p"
                        color="text.secondary"
                        sx={{
                          lineHeight: 1.2,
                          mt: 0.5,
                        }}
                      >
                        {response.code || 'No ODS code recorded'}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={4} sm={4} md={1.5}>
                  <SiteFeatureCard
                    label="Status"
                    value={isInitiated ? 'Initiated' : 'Uninitiated'}
                    enabled={isInitiated}
                  />
                </Grid>

                <Grid item xs={4} sm={4} md={1.5}>
                  <SiteFeatureCard
                    label="Provider"
                    value={response.provider || 'Unknown'}
                    enabled={Boolean(response.provider)}
                  />
                </Grid>

                <Grid item xs={4} sm={4} md={1.5}>
                  <SiteFeatureCard
                    label="ODS Code"
                    value={response.code || 'Missing'}
                    enabled={Boolean(response.code)}
                  />
                </Grid>
              </Grid>

              <Grid container spacing={3} mt={1}>
                <Grid item xs={12} md={6}>
                  <Paper
                    elevation={4}
                    sx={{
                      bgcolor: isInitiated ? 'success.lighter' : 'warning.lighter',
                      border: 1,
                      borderColor: isInitiated ? 'success.light' : 'warning.light',
                      overflow: 'hidden',
                    }}
                  >
                    <Box p={2}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          minWidth: 0,
                          gap: 1,
                          mb: 1,
                        }}
                      >
                        <Box
                          sx={{
                            width: 36,
                            height: 36,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: 'background.paper',
                            flexShrink: 0,
                          }}
                        >
                          <LocalHospitalOutlined color={toneColor} />
                        </Box>

                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="h6" component="h2" sx={{ fontWeight: 700, m: 0 }}>
                            Site Details
                          </Typography>

                          <Typography variant="body2" component="p" color="text.secondary">
                            Site status, provider and ODS information
                          </Typography>
                        </Box>
                      </Box>

                      <Box mt={2}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            mb: 1,
                          }}
                        >
                          <BadgeOutlined color={toneColor} fontSize="small" />

                          <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 700 }}>
                            Site Identifiers
                          </Typography>
                        </Box>

                        <DetailRow label="Site Name" value={response.name} />
                        <DetailRow label="ODS Code" value={response.code} />
                        <DetailRow label="Initiation Status" value={renderSiteStatus(response.initiated)} />
                      </Box>

                      <Divider sx={{ my: 2 }} />

                      <Box mt={2}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            mb: 1,
                          }}
                        >
                          <SourceOutlined color={toneColor} fontSize="small" />

                          <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 700 }}>
                            Site System
                          </Typography>
                        </Box>

                        <DetailRow label="Provider" value={response.provider} />
                      </Box>
                    </Box>
                  </Paper>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Paper
                    elevation={4}
                    sx={{
                      bgcolor: 'background.paper',
                      border: 1,
                      borderColor: 'divider',
                      overflow: 'hidden',
                    }}
                  >
                    <Box p={2}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          minWidth: 0,
                          gap: 1,
                          mb: 1,
                        }}
                      >
                        <Box
                          sx={{
                            width: 36,
                            height: 36,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: 'primary.lighter',
                            flexShrink: 0,
                          }}
                        >
                          <PinDropOutlined color="primary" />
                        </Box>

                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="h6" component="h2" sx={{ fontWeight: 700, m: 0 }}>
                            Site Address
                          </Typography>

                          <Typography variant="body2" component="p" color="text.secondary">
                            Registered site address details
                          </Typography>
                        </Box>
                      </Box>

                      <Box mt={2}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            mb: 1,
                          }}
                        >
                          <BusinessOutlined color="primary" fontSize="small" />

                          <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 700 }}>
                            Address
                          </Typography>
                        </Box>

                        {addressLines.length > 0 ? (
                          <Box component="address" sx={{ fontStyle: 'normal' }}>
                            {addressLines.map((line) => (
                              <Typography key={line} variant="body1" component="p" align="left" mb={0.5}>
                                {line}
                              </Typography>
                            ))}
                          </Box>
                        ) : (
                          <Typography variant="body1" component="p" color="text.secondary">
                            Not recorded
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          </Paper>
        </Box>
      )}
    </PortalLayout>
  );
}

export default SiteScene;
